import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Chef.css";

const API = "https://salathai.co.tz/mrms/Orders.php";

export default function Chef() {
  const navigate = useNavigate();
  
  const [token, setToken] = useState(null);
  const [staff, setStaff] = useState(null);
  const [orders, setOrders] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const menuRef = useRef();
  const intervalRef = useRef(null);

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem("token");
      if (!storedToken) {
        navigate("/");
        return;
      }
      setToken(storedToken);
      
      const staffData = localStorage.getItem("staff");
      if (staffData) {
        try {
          const parsedStaff = JSON.parse(staffData);
          setStaff(parsedStaff);
        } catch (parseErr) {
          console.error("Failed to parse staff data:", parseErr);
          localStorage.removeItem("staff");
          setTimeout(() => navigate("/"), 2000);
          return;
        }
      }
      
      setInitialized(true);
    } catch (err) {
      console.error("Initialization error:", err);
      setError("Failed to initialize application");
      setTimeout(() => navigate("/"), 2000);
    }
  }, [navigate]);

  useEffect(() => {
    if (initialized && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(console.warn);
    }
  }, [initialized]);

  useEffect(() => {
    if (!initialized) return;
    
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [initialized]);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForComparison = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (err) {
      return null;
    }
  };

  const fetchOrders = useCallback(async () => {
    if (!token || !initialized) return;

    try {
      setError(null);
      setLoading(true);
      
      const res = await fetch(`${API}?chef=true`, {
        method: "GET",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.clear();
        navigate("/");
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      let ordersArray = Array.isArray(data) ? data : [];

      const todayDate = getTodayDate();

      // Show only PENDING orders from today (READY orders go straight to payment)
      ordersArray = ordersArray.filter(order => {
        if (!order.created_at) return false;
        
        const orderDateStr = formatDateForComparison(order.created_at);
        if (!orderDateStr) return false;
        
        const isToday = orderDateStr === todayDate;
        const isPending = order.status === "PENDING";
        
        return isToday && isPending;
      });
      
      ordersArray.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });
      
      setOrders(ordersArray);
      
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Unable to load orders. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [token, navigate, initialized]);

  useEffect(() => {
    if (!initialized || !token) return;
    
    fetchOrders();
    
    intervalRef.current = setInterval(() => {
      fetchOrders();
    }, 5000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchOrders, initialized, token]);

  const logout = () => {
    try {
      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
      window.location.href = "/";
    }
  };

  if (!initialized) {
    return (
      <div className={`chef-page ${darkMode ? "dark" : "light"}`}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Initializing...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className={`chef-page ${darkMode ? "dark" : "light"}`}>
        <div className="error-container">
          <div className="error-icon">!</div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="btn-retry">
              Retry
            </button>
            <button onClick={logout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && orders.length === 0) {
    return (
      <div className={`chef-page ${darkMode ? "dark" : "light"}`}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  const pendingCount = orders.length;

  return (
    <div className={`chef-page ${darkMode ? "dark" : "light"}`}>
      <header className="chef-header">
        <div className="logo-section">
          <div className="logo-text">Kitchen Dashboard</div>
        </div>

        <div className="header-actions">
          <button
            className="theme-toggle"
            onClick={() => setDarkMode(!darkMode)}
            aria-label="Toggle theme"
          >
            {darkMode ? "Light" : "Dark"}
          </button>

          <div className="user-menu" ref={menuRef}>
            <button
              className="user-avatar"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="User menu"
            >
              <span className="avatar-initials">
                {staff?.full_name ? staff.full_name.charAt(0).toUpperCase() : "U"}
              </span>
            </button>

            {showMenu && (
              <div className="dropdown-menu">
                <div className="user-info">
                  <strong>{staff?.full_name || "Staff Member"}</strong>
                  <small>Chef</small>
                </div>
                <hr />
                <button onClick={logout} className="logout-btn">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-value">{orders.length}</span>
            <span className="stat-label">Active Orders</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-value">{pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
      </div>

      <div className="orders-container">
        <div className="section-header">
          <h2>Active Orders</h2>
          <div className="date-badge">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <button className="refresh-btn" onClick={fetchOrders}>
            Refresh
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <h3>All Caught Up</h3>
            <p>No active orders at the moment.</p>
            <p style={{ color: "#6b7280", fontSize: "14px", marginTop: "8px" }}>
              Orders are automatically marked as READY when created.
            </p>
          </div>
        ) : (
          <div className="orders-grid">
            {orders.map(order => (
              <div key={order.id} className={`order-card pending`}>
                <div className="order-header">
                  <div className="order-number">
                    #{order.order_number || 'N/A'}
                  </div>
                  <span className="badge pending">
                    {order.status || 'PENDING'}
                  </span>
                </div>

                <div className="order-details">
                  <div className="detail-item">
                    <span className="detail-label">Waiter:</span>
                    <span className="detail-value">{order.staff_name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">
                      {order.created_at ? new Date(order.created_at).toLocaleTimeString() : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Items:</span>
                    <span className="detail-value items-list">
                      {order.items_summary || 'No items'}
                    </span>
                  </div>
                </div>

                <div className="order-actions">
                  <div style={{
                    padding: "12px",
                    background: "#fef3c7",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#92400e",
                    textAlign: "center",
                    fontWeight: "500"
                  }}>
                    ⏳ Waiting for preparation...
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="info-footer">
        <p className="info-message">
          Orders are automatically sent to payment once created.
        </p>
      </div>
    </div>
  );
}